import type { LocalizedDict } from '../index'

export type MessagingMode = 'off' | 'read_only' | 'live'

export const MESSAGING_MODES = [
  'off',
  'read_only',
  'live',
] as const satisfies readonly MessagingMode[]

type MessagingModeCopy = {
  label: string
  description: string
}

type MessagingRolloutStrings = {
  title: string
  description: string
  current: string
  pending: string
  reviewChange: string
  loading: string
  permissionDenied: string
  loadError: string
  retry: string
  saved: string
  realtimeNotice: string
  confirm: {
    title: string
    description: string
    cancel: string
    submit: string
    saving: string
  }
  modes: Record<MessagingMode, MessagingModeCopy>
}

export const messagingRolloutI18n: LocalizedDict<MessagingRolloutStrings> = {
  en: {
    title: 'Messaging operational mode',
    description:
      'Control platform-wide Messaging availability. Server-derived capabilities and tenant isolation still determine what each authorized user can do.',
    current: 'Current',
    pending: 'Pending',
    reviewChange: 'Review change',
    loading: 'Loading Messaging mode…',
    permissionDenied:
      'You do not have permission to manage the Messaging operational mode.',
    loadError:
      'Messaging mode could not be loaded. The control remains disabled.',
    retry: 'Retry',
    saved: 'Messaging mode was updated successfully.',
    realtimeNotice:
      'Instant delivery is not included yet. This control only governs the Messaging capabilities currently delivered by the backend.',
    confirm: {
      title: 'Review Messaging mode change',
      description:
        'Review the current mode, proposed mode, and practical impact before changing production.',
      cancel: 'Cancel',
      submit: 'Confirm change',
      saving: 'Saving…',
    },
    modes: {
      off: {
        label: 'Off',
        description:
          'Messaging navigation and launchers are hidden for eligible workspaces.',
      },
      read_only: {
        label: 'Read only',
        description:
          'Authorized inboxes remain visible; sends, replies, claims, assignments, notes, and status changes are blocked.',
      },
      live: {
        label: 'Live',
        description:
          'Authorized users may perform only the Messaging actions granted by their server-derived capabilities.',
      },
    },
  },
  tr: {
    title: 'Mesajlaşma operasyon modu',
    description:
      'Platform genelindeki Mesajlaşma erişimini yönetin. Her yetkili kullanıcının yapabileceklerini sunucunun belirlediği yetkiler ve tenant izolasyonu sınırlamaya devam eder.',
    current: 'Mevcut',
    pending: 'Bekleyen',
    reviewChange: 'Değişikliği incele',
    loading: 'Mesajlaşma modu yükleniyor…',
    permissionDenied:
      'Mesajlaşma operasyon modunu yönetme yetkiniz yok.',
    loadError:
      'Mesajlaşma modu yüklenemedi. Kontrol devre dışı kalır.',
    retry: 'Tekrar dene',
    saved: 'Mesajlaşma modu başarıyla güncellendi.',
    realtimeNotice:
      'Anında teslim henüz dahil değildir. Bu kontrol yalnızca backend tarafından şu anda sunulan Mesajlaşma yeteneklerini yönetir.',
    confirm: {
      title: 'Mesajlaşma modu değişikliğini incele',
      description:
        'Üretimi değiştirmeden önce mevcut modu, önerilen modu ve pratik etkisini inceleyin.',
      cancel: 'İptal',
      submit: 'Değişikliği onayla',
      saving: 'Kaydediliyor…',
    },
    modes: {
      off: {
        label: 'Kapalı',
        description:
          'Uygun çalışma alanlarında Mesajlaşma menüleri ve başlatıcıları gizlenir.',
      },
      read_only: {
        label: 'Salt okunur',
        description:
          'Yetkili gelen kutuları görünür kalır; mesaj gönderme, yanıtlama, sahiplenme, atama, not ve durum değişiklikleri engellenir.',
      },
      live: {
        label: 'Canlı',
        description:
          'Yetkili kullanıcılar yalnızca sunucunun kendilerine verdiği Mesajlaşma yetkilerindeki işlemleri yapabilir.',
      },
    },
  },
}
